/**
 * TaskDrawer — lightweight slide-in panel showing a task's summary,
 * linked commits, and comments. Opens from the dashboard commit widget
 * so users can triage without full-page navigation.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, ArrowRight, Dna, Bot, Link as LinkIcon, MessageSquare, Brain, Compass } from 'lucide-react';
import {
    getTask, getProject, getComments, getTaskCommits,
    type Task, type TaskComment, type TaskCommit,
} from '../packages/api-client/pm-api';
import { isSoftwareProject } from '../utils/projectType';
import { formatDueDate } from '../utils/date';

function commitHref(c: TaskCommit): string | null {
    if (!c.repo_url) return null;
    const base = c.repo_url.replace(/\.git$/, '').replace(/\/$/, '');
    if (base.includes('github.com') || base.includes('gitlab.com') || base.includes('bitbucket.org')) {
        return `${base}/commit/${c.sha}`;
    }
    return null;
}

interface Props {
    taskId: string;
    onClose: () => void;
}

export function TaskDrawer({ taskId, onClose }: Props) {
    const [task, setTask] = useState<Task | null>(null);
    const [comments, setComments] = useState<TaskComment[]>([]);
    const [commits, setCommits] = useState<TaskCommit[]>([]);
    const [loading, setLoading] = useState(true);
    const [showReasoning, setShowReasoning] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setCommits([]);
        getTask(taskId).then(async t => {
            const project = await getProject(t.project_id).catch(() => null);
            const [cs, gs] = await Promise.all([
                getComments(taskId).catch(() => []),
                project && isSoftwareProject(project) ? getTaskCommits(taskId).catch(() => []) : Promise.resolve([]),
            ]);
            if (!cancelled) { setTask(t); setComments(cs); setCommits(gs); }
        }).catch(() => { if (!cancelled) setTask(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [taskId]);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [onClose]);

    const reasoningCount = useMemo(() => comments.filter(c => c.kind === 'llm_reasoning').length, [comments]);
    const visibleComments = comments.filter(c => c.kind !== 'llm_reasoning' || showReasoning);

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
                display: 'flex', justifyContent: 'flex-end',
            }}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    width: 'min(520px, 100vw)', height: '100vh', background: 'var(--color-surface)',
                    borderLeft: '1px solid var(--color-border)', display: 'flex', flexDirection: 'column',
                    boxShadow: '-8px 0 24px rgba(0,0,0,0.25)',
                }}
            >
                <div style={{ padding: 'var(--space-4)', borderBottom: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontWeight: 700, fontSize: 'var(--font-size-md)' }}>Task detail</div>
                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                        {task && (
                            <Link
                                to={`/tasks/${task.id}`}
                                onClick={onClose}
                                className="btn btn-ghost btn-sm"
                            >
                                Open full <ArrowRight size={14} aria-hidden="true" style={{ verticalAlign: 'middle' }} />
                            </Link>
                        )}
                        <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close"><X size={16} aria-hidden="true" /></button>
                    </div>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                    {loading && <div className="spinner" />}

                    {!loading && task && (
                        <>
                            <div>
                                <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600 }}>{task.title}</div>
                                <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', marginTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    <span>Status: <strong>{task.status}</strong></span>
                                    <span>·</span>
                                    <span>Priority: <strong>{task.priority}</strong></span>
                                    {task.due_date && <><span>·</span><span>Due {formatDueDate(task.due_date)}</span></>}
                                </div>
                                {task.description && (
                                    <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--font-size-sm)', whiteSpace: 'pre-wrap' }}>
                                        {task.description}
                                    </div>
                                )}
                            </div>

                            {commits.length > 0 && (
                                <div>
                                    <div style={{ fontWeight: 600, marginBottom: 'var(--space-2)', display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <Dna size={16} aria-hidden="true" /> Linked commits ({commits.length})
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                        {commits.map(c => {
                                            const href = commitHref(c);
                                            const short = c.sha.length > 7 ? c.sha.slice(0, 7) : c.sha;
                                            return (
                                                <div key={c.id} style={{ display: 'flex', gap: 'var(--space-2)', padding: 'var(--space-2)', background: 'var(--color-surface-2)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
                                                    <code title={c.sha} style={{ fontSize: 12, padding: '2px 6px', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 4 }}>
                                                        {href
                                                            ? <a href={href} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>{short}</a>
                                                            : short}
                                                    </code>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ fontSize: 'var(--font-size-sm)' }}>
                                                            {c.message || <span style={{ color: 'var(--color-text-muted)' }}>(no message)</span>}
                                                        </div>
                                                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                                                            {c.branch && <>{c.branch} · </>}
                                                            {new Date(c.created_at).toLocaleString()}
                                                            {c.author_agent && <> · <Bot size={12} aria-hidden="true" style={{ verticalAlign: 'middle' }} /> {c.author_agent}</>}
                                                            {c.pr_url && (
                                                                <> · <a href={c.pr_url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', textDecoration: 'none' }}><LinkIcon size={12} aria-hidden="true" style={{ verticalAlign: 'middle' }} /> PR</a></>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            <div>
                                <div style={{ fontWeight: 600, marginBottom: 'var(--space-2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><MessageSquare size={16} aria-hidden="true" /> Comments ({comments.length})</span>
                                    {reasoningCount > 0 && (
                                        <button className="btn btn-ghost btn-sm" style={{ fontSize: 'var(--font-size-xs)', display: 'inline-flex', alignItems: 'center', gap: 4 }} onClick={() => setShowReasoning(v => !v)}>
                                            {showReasoning ? 'Hide' : 'Show'} <Brain size={14} aria-hidden="true" /> ({reasoningCount})
                                        </button>
                                    )}
                                </div>
                                {visibleComments.length === 0 && (
                                    <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>No comments yet.</div>
                                )}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                    {visibleComments.map(c => {
                                        const isAI = c.kind === 'llm_reasoning' || c.kind === 'llm_summary';
                                        const isReasoning = c.kind === 'llm_reasoning';
                                        const BadgeIcon = c.kind === 'llm_summary' ? Compass : Brain;
                                        const borderColor = 'var(--accent)';
                                        return (
                                            <div key={c.id} style={{
                                                padding: 'var(--space-2) var(--space-3)', background: 'var(--color-surface-2)',
                                                borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)',
                                                borderLeft: isAI ? `3px solid ${borderColor}` : undefined,
                                                opacity: isReasoning ? 0.8 : 1,
                                            }}>
                                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: 4 }}>
                                                    {isAI
                                                        ? <><BadgeIcon size={12} aria-hidden="true" style={{ verticalAlign: 'middle' }} /> {c.author_agent || (c.kind === 'llm_summary' ? 'AI summary' : 'AI reasoning')}</>
                                                        : <>{c.author_first_name} {c.author_last_name || c.author_email}</>}
                                                    {' · '}
                                                    {new Date(c.created_at).toLocaleString()}
                                                </div>
                                                <div style={{ fontSize: 'var(--font-size-sm)', whiteSpace: 'pre-wrap', fontStyle: isReasoning ? 'italic' : 'normal' }}>
                                                    {c.body}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
